import apiClient, { parseTotalCount } from '@/apiClient';
import type { IDocument } from '@/interfaces';
import type { IPatient, IPatientCreate, IPatientUpdate, IMinimalPatient } from './types';

export interface IIsDuplicateResponse {
  is_duplicate: boolean;
  duplicate_type?: string;
  patient_id?: number;
  first_name?: string;
  last_name?: string;
}

export interface IPatientListParams {
  skip?: number;
  limit?: number;
  search_text?: string | null;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  exclude_disabled?: boolean;
  is_disabled?: boolean;
}

export const patientsApi = {
  sendEmail: (patientId: number, subject: string, messageText: string, documentIds: number[] = []) =>
    apiClient.post(`patients/${patientId}/send_email`, {
      subject, message_text: messageText, document_ids: documentIds,
    }),

  getDocuments: (patientId: number) =>
    apiClient.get<IDocument[]>(`patients/${patientId}/documents`),

  getAll: (params: IPatientListParams = {}) =>
    apiClient.get<IPatient[]>('patients/', {
      params: {
        skip: 0, limit: 100, search_text: '', sort_by: 'last_name', sort_order: 'desc', exclude_disabled: false,
        ...params,
      },
    }),

  getMinimal: (params: IPatientListParams = {}) =>
    apiClient.get<IMinimalPatient[]>('patients/minimal/', {
      params: {
        skip: 0, limit: 100, search_text: '', sort_by: 'last_name', sort_order: 'desc', exclude_disabled: false,
        ...params,
      },
    }),

  getOne: (patientId: number) =>
    apiClient.get<IPatient>(`patients/${patientId}`),

  getOneMinimal: (patientId: number) =>
    apiClient.get<IMinimalPatient>(`patients/minimal/${patientId}`),

  getBySsin: (ssin: string) =>
    apiClient.get<IPatient>(`patients/ssin/${ssin}`),

  create: (data: IPatientCreate) =>
    apiClient.post<IPatient>('patients/', data),

  update: (patientId: number, data: IPatientUpdate) =>
    apiClient.put(`patients/${patientId}`, data),

  disable: (patientId: number) =>
    apiClient.delete(`patients/${patientId}`),

  enable: (patientId: number) =>
    apiClient.get(`patients/${patientId}/enable`),

  merge: (sourcePatientId: number, targetPatientId: number) =>
    apiClient.post('patients/merge', { source_patient_id: sourcePatientId, target_patient_id: targetPatientId }),

  isDuplicate: (params: { ssin?: string; first_name?: string; last_name?: string }) =>
    apiClient.get<IIsDuplicateResponse>('patients/is-duplicate/', { params }),

  async getAllMinimal(params: Omit<IPatientListParams, 'skip' | 'limit'> = {}): Promise<IMinimalPatient[]> {
    const limit = 100;
    const firstResponse = await apiClient.get<IMinimalPatient[]>('patients/minimal/', {
      params: { skip: 0, limit, search_text: '', sort_by: 'last_name', sort_order: 'desc', exclude_disabled: false, ...params },
    });
    const total = parseTotalCount(firstResponse);
    const results: IMinimalPatient[] = [...firstResponse.data];
    const remaining = Math.ceil((total - limit) / limit);
    await Promise.all(
      Array.from({ length: remaining }, (_, i) =>
        apiClient.get<IMinimalPatient[]>('patients/minimal/', {
          params: { skip: (i + 1) * limit, limit, search_text: '', sort_by: 'last_name', sort_order: 'desc', exclude_disabled: false, ...params },
        }).then(r => results.push(...r.data))
      )
    );
    return results;
  },
};
